import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Typography,
  Box,
} from '@mui/material';
import ReactMarkdown from 'react-markdown';

const SAMPLE_WEBHOOK_PAYLOAD = `
\`\`\`json
{
  "event": "transcriptionComplete",
  "file": {
    "id": "b47e9c12-a5d8-4f3e-9d2b-1c8f3e5d9a0f",
    "folderId": "93ab5c24-e8f7-42d1-b6c9-f4d2e8c7a3b0",
    "summary": "[00:00:00] - Product roadmap review: Q1 goals and feature prioritization\n[00:02:15] - Mobile app redesign discussion, focus on user experience\n[00:05:30] - New AI features: Smart summaries and action items extraction\n[00:08:45] - Performance improvements: 40% faster video processing\n[00:12:20] - User feedback analysis and feature requests\n[00:15:00] - Marketing strategy for new AI capabilities\n[00:18:30] - Team structure and resource allocation\n[00:22:45] - Budget review and growth projections\n[00:25:15] - Next steps and action items assignment",
    "url": "https://storage.screenapp.io/meetings/b47e9c12-a5d8-4f3e-9d2b-1c8f3e5d9a0f.mp4",
    "owner": {
      "name": "Sarah Chen",
      "picture": "https://storage.screenapp.io/profiles/sarah-chen.jpg"
    },
    "transcriptUrl": "https://storage.screenapp.io/transcripts/b47e9c12.json",
    "userPromptResponses": {},
    "systemPromptResponses": {
      "SUMMARY_AND_PROMPTS": {
        "promptTitle": "Metadata",
        "responseText": '{"speakers":[{"SPEAKER_01":"Product Manager"},{"SPEAKER_02":"Tech Lead"},{"SPEAKER_03":"Design Lead"}],"title":"Q1 Product Strategy Meeting","summary":"","transcript_type":"Meeting","specificType":"Strategy Planning","purpose":"Quarterly product planning meeting to align on roadmap, discuss new AI features, and plan resource allocation.","prompts":[{"title":"AI Feature Impact","prompt":"Analyze the potential impact of the proposed AI features on user workflow and productivity."},{"title":"Resource Planning","prompt":"Outline the resource requirements for implementing the new features while maintaining existing systems."}]}',
        "createdAt": "2025-02-08T00:30:33.921Z",
        "updatedAt": "2025-02-08T00:30:33.921Z"
      },
      "CHAPTERS": {
        "promptTitle": "Summary",
        "responseText": '{"chapters":[{"title":"Q1 Goals and Roadmap","start":"00:00:00","end":"00:05:30","notes":"Team reviewed Q1 objectives, focusing on AI feature development and mobile app redesign. Key metrics include 30% user growth and 40% improvement in processing speed."},{"title":"AI Feature Development","start":"00:05:30","end":"00:12:20","notes":"Detailed discussion of new AI capabilities including smart summaries, action item extraction, and sentiment analysis. Tech lead presented implementation timeline and resource requirements."},{"title":"User Research Insights","start":"00:12:20","end":"00:18:30","notes":"Analysis of user feedback revealed strong demand for mobile improvements and AI features. Design lead presented user testing results and proposed UX enhancements."},{"title":"Resource Planning","start":"00:18:30","end":"00:25:15","notes":"Team discussed resource allocation, agreed to hire two AI engineers and one mobile developer. Budget approved for Q1 expansion."}]}',
        "createdAt": "2025-02-08T00:30:33.921Z",
        "updatedAt": "2025-02-08T00:30:33.921Z"
      }
    }
  }
}
\`\`\`
`;

interface LearnMoreProps {
  open: boolean;
  onClose: () => void;
  note?: React.ReactNode;
}

const LearnMore: React.FC<LearnMoreProps> = ({ open, onClose, note }) => (
  <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
    <DialogTitle
      sx={{
        fontSize: '1.5rem',
        fontWeight: 700,
        pb: 1,
        color: 'text.primary',
      }}
    >
      Connect to Your Tools
    </DialogTitle>
    <DialogContent>
      {note}
      <DialogContentText component="div">
        <Typography paragraph>
          Automatically send your videos, transcripts, and AI summaries to your
          favorite tools.
        </Typography>

        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h6"
            sx={{
              fontSize: '1.1rem',
              fontWeight: 700,
              color: 'text.primary',
              mb: 2,
              mt: 3,
              borderBottom: '1px solid',
              borderColor: 'divider',
              pb: 1,
            }}
          >
            Types of Webhooks
          </Typography>

          <Box sx={{ mb: 3 }}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}
            >
              Personal Webhooks
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
              Trigger when you save videos to your personal &quot;My
              Recordings&quot; folder. Perfect for individual workflows and
              personal organization.
            </Typography>

            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}
            >
              Team Webhooks
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
              Trigger when videos are saved to team folders or moved from
              &quot;My Recordings&quot; to shared folders. Ideal for team
              collaboration and shared workflows.
            </Typography>
          </Box>

          <Typography
            variant="h6"
            sx={{
              fontSize: '1.1rem',
              fontWeight: 700,
              color: 'text.primary',
              mb: 2,
              mt: 4,
              borderBottom: '1px solid',
              borderColor: 'divider',
              pb: 1,
            }}
          >
            Powerful Integrations with Your Favorite Tools
          </Typography>

          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}
          >
            Team Communication
          </Typography>
          <ul>
            <li>
              Instant Slack notifications with meeting summaries and key
              decisions
            </li>
            <li>Microsoft Teams channels for automated meeting debriefs</li>
            <li>Discord webhooks for real-time collaboration updates</li>
          </ul>

          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 600, mb: 1, mt: 2, color: 'text.primary' }}
          >
            Project Management
          </Typography>
          <ul>
            <li>Create Jira tickets from action items automatically</li>
            <li>Sync meeting notes to Notion workspaces</li>
            <li>Update Linear tasks with meeting decisions</li>
          </ul>

          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 600, mb: 1, mt: 2, color: 'text.primary' }}
          >
            Knowledge Management
          </Typography>
          <ul>
            <li>
              Auto-populate Confluence pages with transcripts and summaries
            </li>
            <li>Create searchable meeting archives in SharePoint</li>
            <li>Build custom workflows with Zapier or Make (Integromat)</li>
          </ul>
        </Box>

        <Typography
          variant="h6"
          sx={{
            fontSize: '1.1rem',
            fontWeight: 700,
            color: 'text.primary',
            mb: 2,
            pb: 1,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          Rich Meeting Data Included
        </Typography>

        <Typography paragraph>
          Every webhook delivers comprehensive meeting data that makes
          integration powerful:
        </Typography>

        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}
        >
          Meeting Intelligence
        </Typography>
        <ul>
          <li>
            Smart chapter breakdowns with key discussion points and decisions
          </li>
          <li>
            Speaker identification with roles (e.g., &quot;Product
            Manager&quot;, &quot;Tech Lead&quot;)
          </li>
          <li>Meeting purpose and type classification</li>
        </ul>

        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 600, mb: 1, mt: 2, color: 'text.primary' }}
        >
          AI Analysis
        </Typography>
        <ul>
          <li>Automated action item extraction for task creation</li>
          <li>Detailed timestamped summaries for easy reference</li>
          <li>Custom analysis prompts for your specific needs</li>
        </ul>

        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 600, mb: 1, mt: 2, color: 'text.primary' }}
        >
          Complete Assets
        </Typography>
        <ul>
          <li>Direct links to video recordings and transcripts</li>
          <li>Structured JSON data for reliable processing</li>
          <li>Real-time delivery as content becomes available</li>
        </ul>

        <Typography
          variant="h6"
          sx={{
            fontSize: '1.1rem',
            fontWeight: 700,
            color: 'text.primary',
            mb: 2,
            pb: 1,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          For Developers
        </Typography>

        <Typography paragraph>
          Our webhooks send detailed data including video files, transcripts,
          AI-generated summaries, and chapter markers. Perfect for building
          custom integrations or advanced automation.
        </Typography>

        <Typography
          variant="h6"
          sx={{
            fontSize: '1.1rem',
            fontWeight: 700,
            color: 'text.primary',
            mb: 2,
            pb: 1,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          Sample Webhook Payload
        </Typography>

        <Box
          sx={{
            bgcolor: 'grey.900',
            color: 'grey.100',
            p: 2,
            borderRadius: 1,
            mb: 2,
            overflowX: 'auto',
          }}
        >
          <ReactMarkdown>{SAMPLE_WEBHOOK_PAYLOAD}</ReactMarkdown>
        </Box>

        <Typography
          variant="h6"
          sx={{
            fontSize: '1.1rem',
            fontWeight: 700,
            color: 'text.primary',
            mb: 2,
            mt: 3,
            pb: 1,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          Technical Features
        </Typography>
        <ul>
          <li>Reliable delivery with automatic retries</li>
          <li>Signed payloads for security verification</li>
          <li>Comprehensive event data for deep integration</li>
          <li>Real-time delivery (average latency &lt;500ms)</li>
        </ul>
      </DialogContentText>
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 2 }}>
      <Button onClick={onClose} variant="contained">
        Got it
      </Button>
    </DialogActions>
  </Dialog>
);

export default LearnMore;
